import React from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, PenLine, UserRound, Send, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

const steps = [
  { key: 'step_upload', icon: Upload },
  { key: 'step_fields', icon: PenLine },
  { key: 'step_details', icon: UserRound },
  { key: 'step_send', icon: Send },
];

const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep }) => {
  const { t } = useTranslation('quicksend');

  return (
    <div className="w-full bg-navy py-6">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={cn(
                      'w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 border-2',
                      isActive && 'bg-green border-green text-white shadow-lg shadow-green/30 scale-110',
                      isCompleted && 'bg-green/20 border-green/40 text-green',
                      !isActive && !isCompleted && 'bg-navy-light border-navy-light text-gray-500'
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium transition-colors hidden sm:block',
                      isActive && 'text-green',
                      isCompleted && 'text-green/70',
                      !isActive && !isCompleted && 'text-gray-500'
                    )}
                  >
                    {t(step.key)}
                  </span>
                </div>

                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'flex-1 h-0.5 mx-3 rounded-full transition-colors duration-300',
                      index < currentStep ? 'bg-green/40' : 'bg-navy-light'
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepIndicator;
